import { economicCalendarAPI } from '../services/api';

export interface CountryRegionalData {
  name: string;
  isoCode: string;
  phoneCode: string;
  currency: string;
  timezone: string[];
  languages: RegionalOption[];
}

export interface RegionalOption {
  value: string;
  label: string;
}

export interface RegionalOptionsFlags {
  country?: boolean;
  currency?: boolean;
  timezone?: boolean;
  language?: boolean;
  currencyValueFormat?: 'upper' | 'lower';
  includeLocalTimezone?: boolean;
}

export interface RegionalOptionsResult {
  countries: CountryRegionalData[];
  countryOptions: RegionalOption[];
  currencyOptions: RegionalOption[];
  timezoneOptions: RegionalOption[];
  languageOptions: RegionalOption[];
}

let countriesCache: CountryRegionalData[] | null = null;
let countriesPromise: Promise<CountryRegionalData[]> | null = null;

const getCountryListFromResponse = (response: any): any[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.countries)) {
    return response.data.countries;
  }

  if (Array.isArray(response?.countries)) {
    return response.countries;
  }

  return [];
};

export const normalizeCountries = (countries: any[]): CountryRegionalData[] =>
  countries
    .map(country => ({
      name: country.name || country.countryName || '',
      isoCode: country.isoCode || '',
      phoneCode: country.phoneCode || '',
      currency: String(country.currency || '')
        .trim()
        .toUpperCase(),
      timezone: Array.isArray(country.timezone)
        ? country.timezone
        : country.timezone
          ? [country.timezone]
          : [],
      languages: Array.isArray(country.languages)
        ? country.languages
            .map((language: any) => ({
              value: String(language.code || language.name || '').trim(),
              label: String(language.name || language.code || '').trim(),
            }))
            .filter(
              (language: RegionalOption) => language.value && language.label
            )
        : [],
    }))
    .filter(country => country.name);

const uniqueOptions = (values: string[]): RegionalOption[] =>
  Array.from(new Set(values.map(value => value.trim()).filter(Boolean)))
    .sort((first, second) => first.localeCompare(second))
    .map(value => ({ value, label: value }));

export const getCurrencyOptionsFromCountries = (
  countries: CountryRegionalData[],
  valueFormat: 'upper' | 'lower' = 'upper'
): RegionalOption[] => {
  const options = uniqueOptions(countries.map(country => country.currency));

  return options.map(option => ({
    ...option,
    value: valueFormat === 'lower' ? option.value.toLowerCase() : option.value,
  }));
};

export const getCountryOptionsFromCountries = (
  countries: CountryRegionalData[]
): RegionalOption[] =>
  countries
    .map(country => ({
      value: country.isoCode || country.name,
      label: country.name,
    }))
    .filter(country => country.value && country.label)
    .sort((first, second) => first.label.localeCompare(second.label));

export const getTimezoneOptionsFromCountries = (
  countries: CountryRegionalData[],
  includeLocal = false
): RegionalOption[] => {
  const timezones = countries.reduce<string[]>(
    (values, country) => [...values, ...country.timezone.map(String)],
    []
  );
  const options = uniqueOptions(timezones);

  return includeLocal
    ? [{ value: 'local', label: 'Local' }, ...options]
    : options;
};

export const getLanguageOptionsFromCountries = (
  countries: CountryRegionalData[]
): RegionalOption[] => {
  const languageMap = new Map<string, RegionalOption>();

  countries.forEach(country => {
    country.languages.forEach(language => {
      const key = language.value || language.label;
      if (!languageMap.has(key)) {
        languageMap.set(key, language);
      }
    });
  });

  return Array.from(languageMap.values()).sort((first, second) =>
    first.label.localeCompare(second.label)
  );
};

export const fetchCountryRegionalOptions = async () => {
  if (countriesCache) {
    return countriesCache;
  }

  if (countriesPromise) {
    return countriesPromise;
  }

  countriesPromise = economicCalendarAPI
    .getCountries()
    .then(response => {
      const countries = normalizeCountries(
        getCountryListFromResponse(response)
      );
      countriesCache = countries;
      return countries;
    })
    .finally(() => {
      countriesPromise = null;
    });

  return countriesPromise;
};

export const fetchRegionalOptions = async ({
  country = false,
  currency = false,
  timezone = false,
  language = false,
  currencyValueFormat = 'upper',
  includeLocalTimezone = false,
}: RegionalOptionsFlags): Promise<RegionalOptionsResult> => {
  const countries = await fetchCountryRegionalOptions();

  return {
    countries,
    countryOptions: country ? getCountryOptionsFromCountries(countries) : [],
    currencyOptions: currency
      ? getCurrencyOptionsFromCountries(countries, currencyValueFormat)
      : [],
    timezoneOptions: timezone
      ? getTimezoneOptionsFromCountries(countries, includeLocalTimezone)
      : [],
    languageOptions: language ? getLanguageOptionsFromCountries(countries) : [],
  };
};
